/**
 * Voice AI — Guest Profile Tab
 * View/edit guest profile fields (name, contact, preferences, personality notes).
 * Dispatches: toast, profile-saved
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';

export class GuestProfileTab extends LitElement {
  static properties = {
    phone: { type: String },
    profile: { type: Object },
    _saving: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, guestStyles];

  constructor() {
    super();
    this.phone = null;
    this.profile = null;
    this._saving = false;
  }

  _val(field) {
    return this.profile?.[field] || '';
  }

  async _save() {
    this._saving = true;
    try {
      const form = this.shadowRoot;
      const data = {
        first_name: form.getElementById('pf-first-name').value.trim(),
        last_name: form.getElementById('pf-last-name').value.trim(),
        email: form.getElementById('pf-email').value.trim(),
        company: form.getElementById('pf-company').value.trim(),
        address: form.getElementById('pf-address').value.trim(),
        city: form.getElementById('pf-city').value.trim(),
        state: form.getElementById('pf-state').value.trim(),
        zip: form.getElementById('pf-zip').value.trim(),
        personality_notes: form.getElementById('pf-personality').value.trim(),
      };

      // Build display_name
      data.display_name = [data.first_name, data.last_name].filter(Boolean).join(' ') || null;

      const result = await guestService.updateProfile(this.phone, data);
      if (result.success) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Profile saved' } }));
        this.dispatchEvent(new CustomEvent('profile-saved'));
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: result.error || 'Save failed', type: 'error' } }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Save failed: ' + err.message, type: 'error' } }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      ${iconStyles}
      <div class="glass-card">
        <div class="section-title">Personal Information</div>
        <div class="section-desc">Basic guest details used by the AI to personalize conversations.</div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">First Name</label>
            <input id="pf-first-name" class="form-input" type="text" .value=${this._val('first_name')} />
          </div>
          <div class="form-group">
            <label class="form-label">Last Name</label>
            <input id="pf-last-name" class="form-input" type="text" .value=${this._val('last_name')} />
          </div>
          <div class="form-group">
            <label class="form-label">Email</label>
            <input id="pf-email" class="form-input" type="email" .value=${this._val('email')} />
          </div>
          <div class="form-group">
            <label class="form-label">Company</label>
            <input id="pf-company" class="form-input" type="text" .value=${this._val('company')} />
          </div>
        </div>
      </div>

      <div class="glass-card">
        <div class="section-title">Address</div>
        <div class="form-grid">
          <div class="form-group" style="grid-column: 1 / -1;">
            <label class="form-label">Street Address</label>
            <input id="pf-address" class="form-input" type="text" .value=${this._val('address')} />
          </div>
          <div class="form-group">
            <label class="form-label">City</label>
            <input id="pf-city" class="form-input" type="text" .value=${this._val('city')} />
          </div>
          <div class="form-group">
            <label class="form-label">State</label>
            <input id="pf-state" class="form-input" type="text" .value=${this._val('state')} />
          </div>
          <div class="form-group">
            <label class="form-label">ZIP Code</label>
            <input id="pf-zip" class="form-input" type="text" .value=${this._val('zip')} />
          </div>
        </div>
      </div>

      <div class="glass-card">
        <div class="section-title">Personality Notes</div>
        <div class="section-desc">Free-text notes about the guest's personality, communication style, or special considerations.</div>
        <textarea id="pf-personality" class="form-textarea" style="width:100%;box-sizing:border-box;"
          .value=${this._val('personality_notes')}
        ></textarea>
      </div>

      <div class="actions-footer">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 1s linear infinite;"></i>` : html`<i class="fa-sharp fa-light fa-check"></i>`}
          Save Profile
        </button>
      </div>
    `;
  }
}

customElements.define('guest-profile-tab', GuestProfileTab);
